'use client'

import { useTranslations } from 'next-intl'

import { TwapUnitInput } from '@/features/order/order-form/components/widget/twap-unit-input'
import { useUnifiedOrderFormContext } from '@/features/order/order-form/lib/order-form-context'
import { useScaleRun } from '@/features/scale/hooks/use-scale-run'
import { scaleLadderErrorMessage } from '@/features/scale/lib/build-scale-params'
import { PRECISE } from '@/features/scale/lib/scale-status'
import { useNumberFormat, useNumberInput } from '@/hooks/use-number-format'

function ScaleInput({
  label,
  value,
  onChange,
  unit,
  placeholder,
  integer,
}: {
  label: string
  value: string
  onChange: (value: string) => void
  unit: string
  placeholder?: string
  integer?: boolean
}) {
  const input = useNumberInput(value, { integer })
  return (
    <label className="flex flex-col gap-1">
      <span className="text-foreground/60 text-xs tracking-tight">{label}</span>
      <TwapUnitInput {...input.bind(onChange)} placeholder={placeholder} unit={unit} />
    </label>
  )
}

/**
 * The Scale tab's own params: the two ladder ends, level count and size ratio, plus a
 * live preview of the levels the server will place (same planner rules, see
 * scale-ladder.ts). Mounted below the shared order rows when isScaleMode.
 */
export function ScaleParams() {
  const t = useTranslations('orderForm')
  const fmt = useNumberFormat()
  const { form } = useUnifiedOrderFormContext()
  const { rowCount, row, instrument, run, fields } = useScaleRun()
  const {
    scaleStartPrice: startPrice,
    scaleEndPrice: endPrice,
    scaleOrderCount: orderCount,
    scaleSizeRatio: sizeRatio,
  } = fields
  const quoteCurrency = instrument?.quoteCurrency ?? ''
  const amountUnit = row?.item.amountUnit ?? ''
  const preview = run?.plan

  const set =
    (name: 'scaleStartPrice' | 'scaleEndPrice' | 'scaleOrderCount' | 'scaleSizeRatio') =>
    (value: string) =>
      form.setValue(name, value, { shouldDirty: true })

  return (
    <div className="flex flex-col gap-3">
      <div className="grid grid-cols-2 gap-2">
        <ScaleInput
          label={t('scaleForm.startPrice')}
          value={startPrice ?? ''}
          onChange={set('scaleStartPrice')}
          unit={quoteCurrency}
        />
        <ScaleInput
          label={t('scaleForm.endPrice')}
          value={endPrice ?? ''}
          onChange={set('scaleEndPrice')}
          unit={quoteCurrency}
        />
        <ScaleInput
          label={t('scaleForm.orderCount')}
          value={orderCount ?? ''}
          onChange={set('scaleOrderCount')}
          unit="#"
          placeholder="5"
          integer
        />
        <ScaleInput
          label={t('scaleForm.sizeRatio')}
          value={sizeRatio ?? ''}
          onChange={set('scaleSizeRatio')}
          unit="×"
          placeholder="1"
        />
      </div>
      <p className="text-foreground/40 text-xs">{t('scaleForm.sizeRatioHint')}</p>

      {rowCount > 1 && <p className="text-chart-red text-xs">{t('scaleForm.singleAccountOnly')}</p>}
      {preview && !preview.ok && (
        <p className="text-chart-red text-xs">{scaleLadderErrorMessage(preview.error, t)}</p>
      )}
      {preview?.ok && (
        <div className="border-foreground/10 max-h-60 overflow-auto border-t pt-2">
          <table className="w-full text-right text-xs">
            <thead className="text-foreground/50">
              <tr>
                <th className="pr-2 text-left font-normal">#</th>
                <th className="pr-2 font-normal">{t('scaleForm.price')}</th>
                <th className="font-normal">
                  {t('scaleForm.size')}
                  {amountUnit ? ` (${amountUnit})` : ''}
                </th>
              </tr>
            </thead>
            <tbody>
              {preview.levels.map((level) => (
                <tr key={level.index}>
                  <td className="text-foreground/50 pr-2 text-left">{level.index + 1}</td>
                  <td className="pr-2">{fmt.number(level.price.toString(), PRECISE)}</td>
                  <td>{fmt.number(level.quantity.toString(), { ...PRECISE, sensitive: true })}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
